import Endpoint from './endpoint';
import HttpAdapter from './http_adapter';
import MqttAdapter from './mqtt_adapter';
import ObserverAdapter from './observer_adapter';
import RequestTracker from './request_tracker';

/**
 * StandardGateway represents a "standard" best-practice-based gateway.
 *
 * If you don't need anything special, you should be able to use this directly.
 * Otherwise, extend it or create a copy of it for your custom gateway and tweak
 * as necessary.
 *
 * See configure_gateways.js for numerous examples.
 */
export default class StandardGateway {
  /**
   * Create a StandardGateway
   *
   * @param {*} backend - an http/mqtt backend
   * @param {(string|Object)} endpoint - an endpoint configuration (see the
   * endpoint documentation)
   */
  constructor(backend, endpoint) {
    this.mqtt = new MqttAdapter(backend);
    this.http = new HttpAdapter(backend);
    this.endpoint = new Endpoint(endpoint);

    // requests tracks outstanding subscriptions, so the resources can be
    // refetched on reconnect (in case any broadcasts were missed while
    // disconnected)
    this.requests = new RequestTracker();
    this.requestAlls = new RequestTracker();
  }

  /**
   * Subscribes to the broadcast topic via mqtt then fetches the resource via
   * http.
   *
   * @param {*} [params] - the parameters to fill in the endpoint topic with.
   * @returns {Promise} - a promise that resolves with the json response body
   *
   * @example
   * let gateway = new StandardGateway(backend, 'agents/:agentId');
   *
   * gateway.setObserver({
   *   onBroadcast: handleAgentBroadcast,
   *   onFetchSuccess: handleAgentFetch,
   * });
   *
   * gateway
   *  .request({ agentId: 'my-agent' })
   *  .then(agentDto => console.log(agentDto));
   */
  request(params) {
    // track what has been requested so it can be re-requested on reconnect
    this.requests.track(params);

    return this.subscribe(params).then(() => this.fetch(params));
  }

  /**
   * Subscribes to the broadcast topic via mqtt.
   *
   * @param {*} [params] - the parameters to fill in the endpoint topic
   * @returns {Promise} - a promise that resolves when the subscription is ack'd
   *
   * @example
   * let gateway = new StandardGateway(backend, 'agents/:agentId');
   *
   * gateway.setObserver({ onBroadcast: handleAgentBroadcast });
   *
   * gateway.subscribe({ agentId: 'my-agent' }).then(() => console.log('subscribed!'));
   */
  subscribe(params) {
    const endpoint = this.endpoint.set(params);
    return this.mqtt
      .subscribe(endpoint.broadcastTopic, this.observer.onBroadcast)
      .then(() => this.mqtt.subscribe(endpoint.broadcastDeleteTopic, this.observer.onBroadcastDelete));
  }

  /**
   * Unsubscribes from the broadcast topic via mqtt.
   *
   * @param {*} [params] - the parameters to fill in the endpoint topic
   * @returns {Promise} - a promise that resolves immediately
   *
   * @example
   * let gateway = new StandardGateway(backend, 'agents/:agentId');
   *
   * gateway.setObserver({ onBroadcast: handleAgentBroadcast });
   *
   * gateway
   *   .subscribe({ agentId: 'my-agent' })
   *   .then(() => gateway.unsubscribe({ agentId: 'my-agent '}));
   */
  unsubscribe(params) {
    // remove any tracked requests so they are not re-fetched on reconnect
    this.requests.remove(params);

    const endpoint = this.endpoint.set(params);
    return this.mqtt
      .unsubscribe(endpoint.broadcastTopic, this.observer.onBroadcast)
      .then(() => this.mqtt.unsubscribe(endpoint.broadcastDeleteTopic, this.observer.onBroadcastDelete));
  }

  /**
   * Uses http to GET the data at the endpoint's `fetchAllUrl`. See the Endpoint
   * documentation for how to configure an endpoint.
   *
   * @param {*} [params] - the parameters to fill in the endpoint url
   * @returns {Promise} - a promise that resolves with the json response body
   *
   * @example
   * let gateway = new StandardGateway(backend, {
   *   fetchAllUrl: '/api/v1/agents',
   * });
   *
   * gateway.setObserver({
   *   onFetchAllSuccess: handleFetchAllAgents,
   *   onFetchAllError: handleFetchAllAgentsError,
   * });
   *
   * gateway
   *   .fetchAll()
   *   .then(allAgentsDto => console.log(allAgentsDto));
   */
  fetchAll(params) {
    let url = this.endpoint.set(params).fetchAllUrl;
    let resource = this.endpoint.patterns.fetchAllUrl;
    return this.http
      .get(url, undefined, resource)
      .then(dto => {
        this.observer.onFetchAllSuccess(dto, params);
        return dto;
      })
      .catch(error => {
        this.observer.onFetchAllError(error, params);
        throw error;
      });
  }

  /**
   * Uses http to GET the data at the endpoint's `fetchUrl`. See the Endpoint
   * documentation for how to configure an endpoint.
   *
   * @param {*} params - the parameters to fill in the endpoint url
   * @returns {Promise} - a promise that resolves with the json response body
   *
   * @example
   * let gateway = new StandardGateway(backend, {
   *   fetchUrl: '/api/v1/agents/:agentId',
   * });
   *
   * gateway.setObserver({
   *   onFetchSuccess: handleFetchAgent,
   *   onFetchError: handleFetchAgentError,
   * });
   *
   * gateway
   *   .fetch({ agentId: 'my-agent-id' })
   *   .then(agentDto => console.log(agentDto));
   */
  fetch(params) {
    let url = this.endpoint.set(params).fetchUrl;
    let resource = this.endpoint.patterns.fetchUrl;
    return this.http
      .get(url, undefined, resource)
      .then(dto => {
        this.observer.onFetchSuccess(dto, params);
        return dto;
      })
      .catch(error => {
        this.observer.onFetchError(error, params);
        throw error;
      });
  }

  /**
   * Uses http to POST the data to the endpoint's `addUrl`. See the Endpoint
   * documentation for how to configure an endpoint.
   *
   * @param {*} [params] - the parameters to fill in the endpoint url
   * @param {*} data - the data to POST to the url
   * @returns {Promise} - a promise that resolves with the json response body
   *
   * @example
   * let gateway = new StandardGateway(backend, {
   *   addUrl: '/api/v1/agents',
   * });
   *
   * gateway.setObserver({
   *   onAddSuccess: handleAddAgent,
   *   onAddError: handleAddAgentError,
   * });
   *
   * gateway
   *   .add({ id: 'my-id', name: 'My name' })
   *   .then(() => console.log('added!'));
   */
  add(params, data) {
    let url = this.endpoint.set(params).addUrl;
    let resource = this.endpoint.patterns.addUrl;
    return this.http
      .post(url, data || params, undefined, resource)
      .then(dto => {
        this.observer.onAddSuccess(dto, params);
        return dto;
      })
      .catch(error => {
        this.observer.onAddError(error, params);
        throw error;
      });
  }

  /**
   * Uses http to PUT the data to the endpoint's `replaceUrl`. See the Endpoint
   * documentation for how to configure an endpoint.
   *
   * By convention, a replace should include an entire new copy of the entity,
   * not a partial update (use `update` for that).
   *
   * @param {*} [params] - the parameters to fill in the endpoint url
   * @param {*} data - the data to PUT to the url
   * @returns {Promise} - a promise that resolves with the json response body
   *
   * @example
   * let gateway = new StandardGateway(backend, {
   *   replaceUrl: '/api/v1/agents/:agentId',
   * });
   *
   * gateway.setObserver({
   *   onReplaceSuccess: handleReplaceAgent,
   *   onReplaceError: handleReplaceAgentError,
   * });
   *
   * gateway
   *   .replace({ agentId: 'my-id' }, { id: 'my-id', name: 'My new name' })
   *   .then(() => console.log('replaced!'));
   */
  replace(params, data) {
    let url = this.endpoint.set(params).replaceUrl;
    let resource = this.endpoint.patterns.replaceUrl;
    return this.http
      .put(url, data || params, undefined, resource)
      .then(dto => {
        this.observer.onReplaceSuccess(dto, params);
        return dto;
      })
      .catch(error => {
        this.observer.onReplaceError(error, params);
        throw error;
      });
  }

  /**
   * Uses http to PATCH the data to the endpoint's `updateUrl`. See the Endpoint
   * documentation for how to configure an endpoint.
   *
   * By convention, the update should only publish that attributes that have
   * changed. Use a `replace` to replace an entire entity with a new version.
   *
   * @param {*} [params] - the parameters to fill in the endpoint url
   * @param {*} data - the data to PATCH to the url
   * @returns {Promise} - a promise that resolves with the json response body
   *
   * @example
   * let gateway = new StandardGateway(backend, {
   *   updateUrl: '/api/v1/agents/:agentId',
   * });
   *
   * gateway.setObserver({
   *   onUpdateSuccess: handleUpdateAgent,
   *   onUpdateError: handleUpdateAgentError,
   * });
   *
   * gateway
   *   .update({ agentId: 'my-id' }, { name: 'My new name' })
   *   .then(() => console.log('updated!'));
   */
  update(params, data) {
    let url = this.endpoint.set(params).updateUrl;
    let resource = this.endpoint.patterns.updateUrl;
    return this.http
      .patch(url, data || params, undefined, resource)
      .then(dto => {
        this.observer.onUpdateSuccess(dto, params);
        return dto;
      })
      .catch(error => {
        this.observer.onUpdateError(error, params);
        throw error;
      });
  }

  /**
   * Issues an http DELETE to the endpoint's `deleteUrl`. See the Endpoint
   * documentation for how to configure an endpoint.
   *
   * @param {*} [params] - the parameters to fill in the endpoint url
   * @returns {Promise} - a promise that resolves with the json response body
   *
   * @example
   * let gateway = new StandardGateway(backend, {
   *   deleteUrl: '/api/v1/agents/:agentId',
   * });
   *
   * gateway.setObserver({
   *   onDeleteSuccess: handleDeleteAgent,
   *   onDeleteError: handleDeleteAgentError,
   * });
   *
   * gateway
   *   .delete({ agentId: 'my-id' })
   *   .then(() => console.log('deleted!'));
   */
  delete(params) {
    let url = this.endpoint.set(params).deleteUrl;
    let resource = this.endpoint.patterns.deleteUrl;
    return this.http
      .delete(url, undefined, resource)
      .then(dto => {
        this.observer.onDeleteSuccess(dto, params);
        return dto;
      })
      .catch(error => {
        this.observer.onDeleteError(error, params);
        throw error;
      });
  }

  /**
   * Subscribes to the collection broadcast topic via mqtt then fetches the
   * resource via http (using `fetchAll`).
   *
   * @param {*} [params] - the parameters to fill in the endpoint topic with.
   * @returns {Promise} - a promise that resolves with the json response body
   *
   * @example
   * let gateway = new StandardGateway(backend, 'agents/:agentId');
   *
   * gateway.setObserver({
   *   onBroadcast: handleAgentBroadcast,
   *   onFetchSuccess: handleAgentFetch,
   * });
   *
   * gateway
   *  .requestAll()
   *  .then(agentDtos => console.log(agentDtos));
   */
  requestAll(params) {
    // track what has been requested so it can be re-requested on reconnect
    this.requestAlls.track(params);

    return this.subscribeAll(params).then(() => this.fetchAll(params));
  }

  /**
   * Subscribes to the collection broadcast topic via mqtt.
   *
   * @param {*} [params] - the parameters to fill in the endpoint topic
   * @returns {Promise} - a promise that resolves when the subscription is ack'd
   *
   * @example
   * let gateway = new StandardGateway(backend, 'agents/:agentId');
   *
   * gateway.setObserver({ onBroadcast: handleAgentBroadcast });
   *
   * gateway.subscribeAll().then(() => console.log('subscribed!'));
   */
  subscribeAll(params) {
    const endpoint = this.endpoint.set(params);
    return this.mqtt
      .subscribe(endpoint.collectionBroadcastTopic, this.observer.onBroadcast)
      .then(() => this.mqtt.subscribe(endpoint.collectionBroadcastDeleteTopic, this.observer.onBroadcastDelete));
  }

  /**
   * Unsubscribes from the collection broadcast topic via mqtt.
   *
   * @param {*} [params] - the parameters to fill in the endpoint topic
   * @returns {Promise} - a promise that resolves immediately
   *
   * @example
   * let gateway = new StandardGateway(backend, 'agents/:agentId');
   *
   * gateway.setObserver({ onBroadcast: handleAgentBroadcast });
   *
   * gateway
   *   .subscribeAll()
   *   .then(() => gateway.unsubscribeAll());
   */
  unsubscribeAll(params) {
    // remove any tracked requests so they are not re-fetched on reconnect
    this.requestAlls.remove(params);

    const endpoint = this.endpoint.set(params);
    return this.mqtt
      .unsubscribe(endpoint.collectionBroadcastTopic, this.observer.onBroadcast)
      .then(() => this.mqtt.unsubscribe(endpoint.collectionBroadcastDeleteTopic, this.observer.onBroadcastDelete));
  }

  /**
   * These methods will be called on the provided observer when the
   * corresponding event occurs.
   *
   * Note only a single observer may be set on the gateway.
   *
   * @callback dtoCallback
   * @param {*} dto - the response data-transfer object
   *
   * @callback errorCallback
   * @param {*} error - the error
   *
   * @param {Object} observer - the observer object, which optionally contains the below methods
   * @param {dtoCallback} [observer.onBroadcast] - called with the dto on broadcast
   * @param {dtoCallback} [observer.onBroadcastDelete] - called with the success dto on broadcast delete
   * @param {dtoCallback} [observer.onFetchAllSuccess] - called with the dto on fetch all success
   * @param {errorCallback} [observer.onFetchAllError] - called with the error on fetch all error
   * @param {dtoCallback} [observer.onFetchSuccess] - called with the dto on fetch success
   * @param {errorCallback} [observer.onFetchError] - called with the error on fetch error
   * @param {dtoCallback} [observer.onAddSuccess] - called with the dto on add success
   * @param {errorCallback} [observer.onAddError] - called with the error on add error
   * @param {dtoCallback} [observer.onReplaceSuccess] - called with the dto on replace success
   * @param {errorCallback} [observer.onReplaceError] - called with the error on replace error
   * @param {dtoCallback} [observer.onUpdateSuccess] - called with the dto on update success
   * @param {errorCallback} [observer.onUpdateError] - called with the error on update error
   * @param {dtoCallback} [observer.onDeleteSuccess] - called with the dto on delete success
   * @param {errorCallback} [observer.onDeleteError] - called with the error on delete error
   */
  setObserver(observer) {
    if (this._observer) {
      throw new Error('Cannot set an observer more than once on a gateway.');
    }
    this._observer = new ObserverAdapter(observer, this.endpoint.patterns);
  }

  get observer() {
    if (!this._observer) {
      this._observer = new ObserverAdapter({}, this.endpoint.patterns);
    }
    return this._observer;
  }

  // lifecycle methods
  // these methods are used by the backend to communicate changes to the gateway

  // init is called on login with the agent and org ids
  init({ orgId, agentId }) {
    this.endpoint = this.endpoint.set({ orgId, agentId });
  }

  // reconnectHandler is called on reconnect, it should refetch resources that
  // were subscribed to
  reconnectHandler() {
    // refetch every resource that we were subscribed to on reconnect
    let requests = this.requests.map(params => this.request(params));
    let requestAlls = this.requestAlls.map(params => this.requestAll(params));
    return Promise.all(requests.concat(requestAlls));
  }

  // resetHandler is called on logout
  resetHandler() {
    this.endpoint.reset();
    this.requests.reset();
    this.requestAlls.reset();
  }
}



// WEBPACK FOOTER //
// ./src/scripts/adapters/gateways/lib/standard_gateway/standard_gateway.js